import React, { useState, useCallback } from 'react'
import {
  Box, TextField, MenuItem, Button, Typography, Paper, Divider,
  Autocomplete, Alert, Stack, Chip
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'

interface LocationData {
  name: string
  address: string
  latitude: string
  longitude: string
  phone: string
  category: string
  description: string
  website_url: string
  google_place_id: string
  group_ids: number[]
}

interface PlaceOption {
  place_id: string
  name: string
  address: string
  lat: number
  lng: number
}

interface GroupOption {
  id: number
  name: string
  company: string
}

interface Props {
  data: LocationData
  setData: (key: string, value: string | number[]) => void
  categories: string[]
  groups: GroupOption[]
  errors: Record<string, string[]>
  onSubmit: (e: React.FormEvent) => void
  processing: boolean
  submitLabel: string
}

const categoryLabels: Record<string, string> = {
  restaurant: 'Restaurant',
  cafe: 'Cafe',
  filming_location: 'Filming Location',
  practice_studio: 'Practice Studio',
  entertainment_venue: 'Entertainment Venue',
  shop: 'Shop',
  landmark: 'Landmark',
  other: 'Other',
}

export default function LocationForm({ data, setData, categories, groups, errors, onSubmit, processing, submitLabel }: Props) {
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<PlaceOption[]>([])
  const [searching, setSearching] = useState(false)
  const [searchError, setSearchError] = useState<string | null>(null)

  const searchGooglePlaces = useCallback(async (query: string) => {
    if (query.length < 2) return
    setSearching(true)
    setSearchError(null)
    try {
      const params = new URLSearchParams({ query })
      const res = await fetch(`/api/v1/google_maps/search?${params}`)
      const json = await res.json()
      if (json.error) {
        setSearchError(json.error.message)
        return
      }
      setSearchResults(json.places || [])
    } catch {
      setSearchError('Failed to search Google Places')
    } finally {
      setSearching(false)
    }
  }, [])

  const handlePlaceSelect = async (place: PlaceOption | null) => {
    if (!place) return

    try {
      const res = await fetch(`/api/v1/google_maps/details/${place.place_id}`)
      const json = await res.json()
      if (json.place) {
        const p = json.place
        setData('name', p.name || '')
        setData('address', p.address || '')
        setData('latitude', p.lat?.toString() || '')
        setData('longitude', p.lng?.toString() || '')
        setData('phone', p.phone || '')
        setData('website_url', p.website_url || '')
        setData('google_place_id', p.place_id || '')
      }
    } catch {
      setData('name', place.name || '')
      setData('address', place.address || '')
      setData('latitude', place.lat?.toString() || '')
      setData('longitude', place.lng?.toString() || '')
      setData('google_place_id', place.place_id || '')
    }
  }

  const fieldError = (field: string) => errors[field]?.join(', ')

  const selectedGroups = groups.filter((g) => data.group_ids.includes(g.id))

  return (
    <Box component="form" onSubmit={onSubmit}>
      <Paper sx={{ p: 4, mb: 4 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <SearchIcon sx={{ fontSize: 16 }} />
          Search Google Places
        </Typography>
        <Autocomplete
          freeSolo
          options={searchResults}
          filterOptions={(x) => x}
          getOptionLabel={(option) => typeof option === 'string' ? option : `${option.name} — ${option.address}`}
          inputValue={searchQuery}
          onInputChange={(_e, value) => {
            setSearchQuery(value)
            if (value.length >= 2) {
              searchGooglePlaces(value)
            }
          }}
          onChange={(_e, value) => {
            if (value && typeof value !== 'string') {
              handlePlaceSelect(value)
            }
          }}
          loading={searching}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search for a place..."
              placeholder="Type a place name or address"
            />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option.place_id}>
              <Box>
                <Typography variant="body1">{option.name}</Typography>
                <Typography variant="caption" color="text.secondary">{option.address}</Typography>
              </Box>
            </li>
          )}
        />
        {searchError && <Alert severity="error" sx={{ mt: 1 }}>{searchError}</Alert>}
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Select a place to auto-fill the form, or enter details manually below.
        </Typography>
      </Paper>

      <Paper sx={{ p: 4 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 3 }}>Location Details</Typography>

        <Stack spacing={2.5}>
          <TextField
            label="Name"
            value={data.name}
            onChange={(e) => setData('name', e.target.value)}
            required
            fullWidth
            error={!!fieldError('name')}
            helperText={fieldError('name')}
          />

          <TextField
            label="Address"
            value={data.address}
            onChange={(e) => setData('address', e.target.value)}
            fullWidth
            error={!!fieldError('address')}
            helperText={fieldError('address')}
          />

          <Stack direction="row" spacing={2}>
            <TextField
              label="Latitude"
              value={data.latitude}
              onChange={(e) => setData('latitude', e.target.value)}
              type="number"
              fullWidth
              slotProps={{ htmlInput: { step: 'any', min: -90, max: 90 } }}
              error={!!fieldError('latitude')}
              helperText={fieldError('latitude')}
            />
            <TextField
              label="Longitude"
              value={data.longitude}
              onChange={(e) => setData('longitude', e.target.value)}
              type="number"
              fullWidth
              slotProps={{ htmlInput: { step: 'any', min: -180, max: 180 } }}
              error={!!fieldError('longitude')}
              helperText={fieldError('longitude')}
            />
          </Stack>

          <TextField
            label="Phone"
            value={data.phone}
            onChange={(e) => setData('phone', e.target.value)}
            fullWidth
          />

          <TextField
            label="Website URL"
            value={data.website_url}
            onChange={(e) => setData('website_url', e.target.value)}
            fullWidth
          />

          <TextField
            select
            label="Category"
            value={data.category}
            onChange={(e) => setData('category', e.target.value)}
            fullWidth
            error={!!fieldError('category')}
            helperText={fieldError('category')}
          >
            <MenuItem value="">No category</MenuItem>
            {categories.map((cat) => (
              <MenuItem key={cat} value={cat}>
                {categoryLabels[cat] || cat}
              </MenuItem>
            ))}
          </TextField>

          <Autocomplete
            multiple
            options={groups}
            value={selectedGroups}
            onChange={(_e, value) => setData('group_ids', value.map((g) => g.id))}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const { key, ...chipProps } = getTagProps({ index })
                return <Chip key={key} variant="outlined" label={option.name} size="small" {...chipProps} />
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Groups"
                placeholder="Assign K-pop groups to this location"
                error={!!fieldError('group_ids')}
                helperText={fieldError('group_ids') || 'Search and select one or more groups'}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Box>
                  <Typography variant="body2">{option.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{option.company}</Typography>
                </Box>
              </li>
            )}
          />

          <TextField
            label="Description"
            value={data.description}
            onChange={(e) => setData('description', e.target.value)}
            multiline
            rows={3}
            fullWidth
            placeholder="Why is this place notable for K-pop fans?"
          />

          {data.google_place_id && (
            <TextField
              label="Google Place ID"
              value={data.google_place_id}
              disabled
              fullWidth
            />
          )}
        </Stack>

        <Divider sx={{ my: 3 }} />

        <Button type="submit" variant="contained" disabled={processing}>
          {processing ? 'Saving...' : submitLabel}
        </Button>
      </Paper>
    </Box>
  )
}
